// ─────────────────────────────────────────────────────────────────────────────
// StudentReports.jsx
// A searchable student roster. Clicking a student opens their full report
// (profile + fee history) via the shared StudentDetail component.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState, useEffect } from "react";
import { C, fmtClass, fmtSection, Card, CardHeader, TH, TD, Badge, Avatar, Field, inputStyle, selectStyle } from "../fees/feeUI";
import { normalizeStudents, statusColor, PageHeading, Pagination, ReportToolbar, useAdminCampus } from "./reportsShared";
import { printReport, exportCsv } from "./reportExport";
import StudentDetail from "./StudentDetail";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";
import {
  classSessionIdsOf,
  uniqueClassNames,
  sectionsForClassSession,
} from "../../../utils/campusClasses";

const PER_PAGE = 12;

const StudentReports = ({ students = [] }) => {
  const [selected, setSelected] = useState(null);
  const [search,   setSearch]   = useState("");
  const [classF,   setClassF]   = useState("all");
  const [sectionF, setSectionF] = useState("all");
  const [statusF,  setStatusF]  = useState("all");
  const [sessionF, setSessionF] = useState("all");
  const [sessions, setSessions] = useState([]);
  const [campusClasses, setCampusClasses] = useState([]);
  const [page,     setPage]     = useState(1);

  const norm = useMemo(() => normalizeStudents(students), [students]);

  // Sessions and classes come from campus setup so filters match Class Setup.
  useEffect(() => {
    let alive = true;
    const campusIds = [...new Set(students.map((s) => s.campus_id).filter(Boolean))];
    const scope = campusIds.length === 1 ? `?campus_id=${campusIds[0]}` : "";

    Promise.all([
      fetch(`${API_BASE_URL}/api/sessions${scope}`, { headers: getAuthHeaders() })
        .then((res) => (res.ok ? res.json() : []))
        .catch(() => []),
      fetch(`${API_BASE_URL}/api/classes${scope}`, { headers: getAuthHeaders() })
        .then((res) => (res.ok ? res.json() : []))
        .catch(() => []),
    ]).then(([sessionData, classData]) => {
      if (!alive) return;
      setSessions(Array.isArray(sessionData) ? sessionData : []);
      setCampusClasses(Array.isArray(classData) ? classData : []);
    });

    return () => { alive = false; };
  }, [students]);

  const sessionMap = useMemo(
    () => new Map(sessions.map((session) => [String(session.id), session])),
    [sessions],
  );

  const classMap = useMemo(
    () => new Map(campusClasses.map((classItem) => [String(classItem.id), classItem])),
    [campusClasses],
  );

  const selectedSessionId = useMemo(() => {
    if (sessionF === "all") return "";
    const match = sessions.find((session) => session.name === sessionF);
    return match ? String(match.id) : "";
  }, [sessionF, sessions]);

  // Fall back to sessions present on the student records when the API returns none.
  const sessionOptions = useMemo(() => {
    if (sessions.length > 0) {
      return [...new Set(sessions.map((session) => session.name).filter(Boolean))];
    }
    return [...new Set(norm.map(s => (s.academic_session || "").trim()).filter(Boolean))].sort();
  }, [sessions, norm]);

  const classes = useMemo(() => {
    if (campusClasses.length) {
      return ["all", ...uniqueClassNames(campusClasses, { sessionId: selectedSessionId })];
    }
    const pool = sessionF === "all"
      ? norm
      : norm.filter((s) => (s.academic_session || "").trim() === sessionF);
    return ["all", ...[...new Set(pool.map(s => s.class).filter(Boolean))].sort()];
  }, [campusClasses, selectedSessionId, norm, sessionF]);

  const sections = useMemo(() => {
    const className = classF === "all" ? "" : classF;
    if (campusClasses.length) {
      return ["all", ...sectionsForClassSession(campusClasses, {
        sessionId: selectedSessionId,
        className,
        students: norm,
        formatSection: fmtSection,
      })];
    }
    const pool = norm.filter((s) => {
      const matchesSession = sessionF === "all" || (s.academic_session || "").trim() === sessionF;
      const matchesClass = classF === "all" || s.class === classF;
      return matchesSession && matchesClass;
    });
    return ["all", ...[...new Set(pool.map((s) => fmtSection(s.section)).filter((s) => s && s !== "—"))].sort()];
  }, [campusClasses, selectedSessionId, classF, norm, sessionF]);

  const studentMatchesSession = (student) => {
    if (sessionF === "all") return true;
    const studentSession = String(student.academic_session || "").trim();
    if (studentSession === sessionF) return true;
    if (selectedSessionId && String(student.academic_session_id || "") === selectedSessionId) return true;

    const linkedClass = classMap.get(String(student.class_id || ""));
    const linkedSessionIds = linkedClass ? classSessionIdsOf(linkedClass) : [];
    if (selectedSessionId && linkedSessionIds.includes(selectedSessionId)) return true;

    const linkedSessionNames = linkedSessionIds
      .map((id) => sessionMap.get(String(id))?.name)
      .filter(Boolean);
    if (linkedSessionNames.some((name) => String(name).trim() === sessionF)) return true;

    if (selectedSessionId && student.class) {
      return campusClasses.some(
        (classItem) =>
          String(classItem.name || "") === String(student.class) &&
          classSessionIdsOf(classItem).includes(selectedSessionId),
      );
    }
    return false;
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return norm.filter(s =>
      studentMatchesSession(s) &&
      (classF === "all" || s.class === classF) &&
      (sectionF === "all" || fmtSection(s.section) === sectionF) &&
      (statusF === "all" || (statusF === "active" ? s.status === "Active" : s.status !== "Active")) &&
      (!q || s.name?.toLowerCase().includes(q) || String(s.rollNo).toLowerCase().includes(q))
    );
  }, [norm, search, classF, sectionF, sessionF, statusF, selectedSessionId, classMap, sessionMap, campusClasses]);

  useEffect(() => {
    setClassF("all");
    setSectionF("all");
  }, [sessionF]);

  useEffect(() => {
    setSectionF("all");
  }, [classF]);

  useEffect(() => {
    if (classF !== "all" && !classes.includes(classF)) setClassF("all");
  }, [classes, classF]);

  useEffect(() => {
    if (sectionF !== "all" && !sections.includes(sectionF)) setSectionF("all");
  }, [sections, sectionF]);

  useEffect(() => { setPage(1); }, [search, classF, sectionF, sessionF, statusF]);

  const safePage = Math.min(page, Math.max(1, Math.ceil(filtered.length / PER_PAGE)));
  const rows     = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const campus = useAdminCampus();

  const listReport = () => ({
    title: "Student Report",
    campus,
    scopeMeta: [
      ["Session", sessionF === "all" ? "All Sessions" : sessionF],
      ["Class", classF === "all" ? "All Classes" : fmtClass(classF)],
      ["Section", sectionF === "all" ? "All Sections" : sectionF],
      ["Status", statusF === "all" ? "All" : statusF === "active" ? "Active" : "Inactive"],
      ["Search", search || "—"],
      ["Students", filtered.length],
    ],
    summaryCards: [
      { label: "Students", value: filtered.length },
      { label: "Active", value: filtered.filter(s => s.status === "Active").length },
      { label: "Inactive", value: filtered.filter(s => s.status !== "Active").length },
    ],
    sections: [{
      heading: "Students",
      headers: ["#", "Student", "Class", "Section", "Roll No.", "Status"],
      rows: filtered.map((s, i) => [i + 1, s.name, fmtClass(s.class), fmtSection(s.section), s.rollNo, s.status]),
    }],
  });

  if (selected) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
        <PageHeading title="Student Reports" subtitle="Full profile and fee history" />
        <StudentDetail student={selected} campus={campus} onBack={() => setSelected(null)} />
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      <PageHeading
        actions={<ReportToolbar onExport={() => exportCsv({ filename: "student-report.csv", ...listReport() })} onPrint={() => printReport(listReport())} />}
      />

      <Card>
        <CardHeader title="Students" right={<Badge color="slate">{filtered.length} students</Badge>} />

        {/* Filters */}
        <div style={{ padding: "14px 20px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
          <Field label="Search"><input style={{ ...inputStyle, minWidth: "220px" }} placeholder="Name or student ID…" value={search} onChange={e => setSearch(e.target.value)} /></Field>
          <Field label="Session">
            <select style={{ ...selectStyle, minWidth: "160px" }} value={sessionF} onChange={e => setSessionF(e.target.value)}>
              <option value="all">All Sessions</option>
              {sessionOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Class">
            <select style={{ ...selectStyle, minWidth: "140px" }} value={classF} onChange={e => setClassF(e.target.value)}>
              {classes.map(c => <option key={c} value={c}>{c === "all" ? "All Classes" : fmtClass(c)}</option>)}
            </select>
          </Field>
          <Field label="Section">
            <select style={{ ...selectStyle, minWidth: "130px" }} value={sectionF} onChange={e => setSectionF(e.target.value)}>
              {sections.map(s => <option key={s} value={s}>{s === "all" ? "All Sections" : fmtSection(s)}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select style={{ ...selectStyle, minWidth: "130px" }} value={statusF} onChange={e => setStatusF(e.target.value)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </Field>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><TH>#</TH><TH>Student</TH><TH>Class</TH><TH>Section</TH><TH>Roll No.</TH><TH>Status</TH><TH></TH></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: "48px", textAlign: "center", color: C.slate, fontSize: "13px" }}>No students match your filters.</td></tr>
              ) : rows.map((s, i) => {
                const rowBg = i % 2 === 0 ? C.white : C.bg;
                return (
                  <tr key={s.id} style={{ background: rowBg, cursor: "pointer" }}
                    onClick={() => setSelected(s)}
                    title="Click to view this student's full report"
                    onMouseEnter={e => { e.currentTarget.style.background = C.blueSoft; }}
                    onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                    <TD><span style={{ color: C.slateLight, fontWeight: 600 }}>{(safePage - 1) * PER_PAGE + i + 1}</span></TD>
                    <TD>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <Avatar name={s.name} />
                        <span style={{ fontWeight: 700 }}>{s.name}</span>
                      </div>
                    </TD>
                    <TD><Badge color="blue">{fmtClass(s.class)}</Badge></TD>
                    <TD>{fmtSection(s.section)}</TD>
                    <TD mono>{s.rollNo}</TD>
                    <TD><Badge color={statusColor(s.status)}>{s.status}</Badge></TD>
                    <TD right><span style={{ color: C.slateLight, display: "inline-flex" }}><svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7"/></svg></span></TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination page={safePage} perPage={PER_PAGE} total={filtered.length} onChange={setPage} />
      </Card>
    </div>
  );
};

export default StudentReports;
